import { StyledFormLabel } from "@kaltura/mediaspace-shared-ui/controlled-form";
import { Controller, useFormContext } from "react-hook-form";
import { Select, Typography } from "@kaltura/ds-react-components";
import { StyledContainer, StyledContent, StyledMenuItem, StyledOption } from "./styledComponents";
import { translate } from "@kaltura/mediaspace-shared-utils";
import PrivacyMenuItemContent from "./privacy-menu-item-content/PrivacyMenuItemContent";
import { useState } from "react";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import { getMenuProps, iconMap } from "./helper";
import { PrivacyType, PrivacyOptionType } from "../types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

export interface PrivacySelectFieldProps {
    /**
     * optional onChange callback
     */
    onChange?: (value: string) => void;
    /**
     * dropdown options data
     */
    optionsData: PrivacyType;
}

const PrivacySelectField = (props: PrivacySelectFieldProps) => {
    const { onChange, optionsData } = props;

    const { options } = optionsData;

    const defaultOption = options.find((option) => option.id === optionsData.defaultOption) ?? options[0];

    const [selectedOption, setSelectedOption] = useState<PrivacyOptionType>(defaultOption);

    const { control } = useFormContext();

    const sendButtonAnalytics = useButtonAnalytics();

    const handleChange = (value: string) => {
        const selectedOption = options.find((option) => option.id === Number(value));
        if (selectedOption) {
            setSelectedOption(selectedOption);
        }
        sendButtonAnalytics(`Set privacy - ${value}`, ButtonClickAnalyticsType.CHOOSE);
        onChange?.(value);
    };

    const generateIcon = (iconName: string) => {
        const IconComponent = iconMap[iconName];
        return <IconComponent />;
    };

    // renderValue is used to display the selected value in the select input
    const generateRenderValue = (selected: string) => {
        const selectedOption = options.find((option) => option.id === Number(selected));

        if (!selectedOption) {
            return null;
        }

        return (
            <StyledOption>
                {generateIcon(selectedOption.icon)}
                {selectedOption.title}
            </StyledOption>
        );
    };

    const theme = useTheme();

    const baseClassName = "kms-ds-create-channel-modal-content-form-privacy";

    return (
        <StyledContainer className={`${baseClassName}`}>
            <StyledFormLabel
                htmlFor={"privacy"}
                text={translate("Privacy")}
                fontWeightBold={true}
                className={`${baseClassName}-label`}
            />
            <StyledContent className={`${baseClassName}-content`}>
                <Controller
                    control={control}
                    name={"privacy" as "dummy"}
                    defaultValue={defaultOption.id}
                    render={({ field: { onChange, onBlur, value } }) => (
                        <Select
                            id={"privacy"}
                            value={value}
                            onChange={(event) => {
                                onChange(event as unknown as string);
                                handleChange(event.target.value as string);
                            }}
                            onBlur={onBlur}
                            fullWidth={false}
                            renderValue={(value: unknown) => generateRenderValue(value as string)}
                            MenuProps={getMenuProps(theme)}
                        >
                            {options.map((option) => (
                                <StyledMenuItem key={option.id} value={option.id}>
                                    <PrivacyMenuItemContent
                                        icon={generateIcon(option.icon)}
                                        title={option.title}
                                        description={option.description}
                                        isSelected={option.id === selectedOption.id}
                                    />
                                </StyledMenuItem>
                            ))}
                        </Select>
                    )}
                />
                <Typography variant={"body2"}>{selectedOption.description}</Typography>
            </StyledContent>
        </StyledContainer>
    );
};

export default PrivacySelectField;
